import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import { analyticsApis } from "@services/apis/Analytics";
import { GetRevenueData } from "@services/models/Analytics/GetRevenueModel";
import { ColDef } from "ag-grid-community";
import { useMemo, useRef, useState } from "react";
import ConstantFunctions from "../../../utils/constants_functions";
import DualAxisChart from "../../revenue/components/DualAxisChartComponent";

const MobileRevenue = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<GetRevenueData[]>([]);
  const gridRef = useRef<any>(null);
  const [typeFilter, setTypeFilter] = useState<"Monthly" | "Daily">("Monthly");
  const constantFunctions = new ConstantFunctions();

  const inFlight = useRef(false);

  const getRevenue = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      setLoading(true);
      setError(null);
      const response =
        typeFilter === "Monthly"
          ? await analyticsApis.getRevenue()
          : await analyticsApis.getRevenueDaily();
      setData(response.data);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  };

  const handleTypeFilterChange = (type: "Monthly" | "Daily") => {
    if (type === typeFilter) return;

    setTypeFilter(type);
    setData([]);
    setError(null);
  };

  const exportToCSV = () => {
    if (gridRef.current) {
      const date = new Date();
      const formattedDate = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      gridRef.current.api.exportDataAsCsv({
        fileName: `revenue-data-${formattedDate}.csv`,
        skipHeader: false,
        skipColumnGroupHeaders: false,
        skipColumnHeaders: false,
        allColumns: true,
        onlySelected: false,
        suppressQuotes: false,
        columnSeparator: ",",
      });
    }
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      typeFilter === "Monthly"
        ? {
            headerName: "Month",
            field: "Month",
            pinned: "left",
            flex: 0,
            width: 90,
            minWidth: 80,
            maxWidth: 100,
            headerClass: "ag-header-cell-custom",
            cellClass: "ag-cell-custom",
          }
        : {
            headerName: "Day",
            field: "Day",
            pinned: "left",
            flex: 0,
            width: 90,
            minWidth: 80,
            maxWidth: 100,
            headerClass: "ag-header-cell-custom",
            cellClass: "ag-cell-custom",
            valueFormatter: (p) => {
              if (!p.value) return "";
              return constantFunctions.dateFormatInDDMMM(p.value);
            },
          },

      {
        headerName: "CPG Global",
        children: [
          {
            headerName: "Pay-In",
            children: [
              {
                headerName: "Revenue",
                field: "CPG_PayIn_Revenue",
                valueFormatter: (p) => constantFunctions.formatRevenue(p.value),
                flex: 1,
                minWidth: 80,
                maxWidth: 120,
                headerClass: "ag-header-cell-custom",
                cellClass: "ag-cell-custom",
              },
              {
                headerName: "Volume",
                field: "CPG_PayIn_Volume",
                valueFormatter: (p) => constantFunctions.formatRevenue(p.value),
                flex: 1,
                minWidth: 80,
                maxWidth: 120,
                headerClass: "ag-header-cell-custom",
                cellClass: "ag-cell-custom",
              },
            ],
          },
          {
            headerName: "Pay-Out",
            children: [
              {
                headerName: "Revenue",
                field: "CPG_PayOut_Revenue",
                valueFormatter: (p) => constantFunctions.formatRevenue(p.value),
                flex: 1,
                minWidth: 80,
                maxWidth: 120,
                headerClass: "ag-header-cell-custom",
                cellClass: "ag-cell-custom",
              },
              {
                headerName: "Volume",
                field: "CPG_PayOut_Volume",
                valueFormatter: (p) => constantFunctions.formatRevenue(p.value),
                flex: 1,
                minWidth: 80,
                maxWidth: 120,
                headerClass: "ag-header-cell-custom",
                cellClass: "ag-cell-custom",
              },
            ],
          },
          {
            headerName: "Total",
            children: [
              {
                headerName: "Revenue",
                field: "CPG_Total_Revenue",
                valueFormatter: (p) => constantFunctions.formatRevenue(p.value),
                flex: 1,
                minWidth: 80,
                maxWidth: 120,
                headerClass: "ag-header-cell-custom",
                cellClass: "ag-cell-custom",
              },
              {
                headerName: "Volume",
                field: "CPG_Total_Volume",
                valueFormatter: (p) => constantFunctions.formatRevenue(p.value),
                flex: 1,
                minWidth: 80,
                maxWidth: 120,
                headerClass: "ag-header-cell-custom",
                cellClass: "ag-cell-custom",
              },
            ],
          },
        ],
      },

      {
        headerName: "Cross-Ramp",
        children: [
          {
            headerName: "India",
            children: [
              {
                headerName: "Pay-In",
                children: [
                  {
                    headerName: "Revenue",
                    field: "XR_India_PayIn_Revenue",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                  {
                    headerName: "Volume",
                    field: "XR_India_PayIn_Volume",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                ],
              },
              {
                headerName: "Pay-Out",
                children: [
                  {
                    headerName: "Revenue",
                    field: "XR_India_PayOut_Revenue",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                  {
                    headerName: "Volume",
                    field: "XR_India_PayOut_Volume",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                ],
              },
              {
                headerName: "Total",
                children: [
                  {
                    headerName: "Revenue",
                    field: "XR_India_Total_Revenue",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                  {
                    headerName: "Volume",
                    field: "XR_India_Total_Volume",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                ],
              },
            ],
          },
          {
            headerName: "Brazil",
            children: [
              {
                headerName: "Pay-In",
                children: [
                  {
                    headerName: "Revenue",
                    field: "XR_BRL_PayIn_Revenue",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                  {
                    headerName: "Volume",
                    field: "XR_BRL_PayIn_Volume",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                ],
              },
              {
                headerName: "Pay-Out",
                children: [
                  {
                    headerName: "Revenue",
                    field: "XR_BRL_PayOut_Revenue",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                  {
                    headerName: "Volume",
                    field: "XR_BRL_PayOut_Volume",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                ],
              },
              {
                headerName: "Total",
                children: [
                  {
                    headerName: "Revenue",
                    field: "XR_BRL_Total_Revenue",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                  {
                    headerName: "Volume",
                    field: "XR_BRL_Total_Volume",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                ],
              },
            ],
          },
          {
            headerName: "Europe",
            children: [
              {
                headerName: "Pay-In",
                children: [
                  {
                    headerName: "Revenue",
                    field: "OpenBanking_PayIn_Revenue",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                  {
                    headerName: "Volume",
                    field: "OpenBanking_PayIn_Volume",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                ],
              },
              {
                headerName: "Pay-Out",
                children: [
                  {
                    headerName: "Revenue",
                    field: "OpenBanking_PayOut_Revenue",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                  {
                    headerName: "Volume",
                    field: "OpenBanking_PayOut_Volume",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                ],
              },
              {
                headerName: "Total",
                children: [
                  {
                    headerName: "Revenue",
                    field: "OpenBanking_Total_Revenue",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                  {
                    headerName: "Volume",
                    field: "OpenBanking_Total_Volume",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                ],
              },
            ],
          },
          {
            headerName: "Canada",
            children: [
              {
                headerName: "Pay-In",
                children: [
                  {
                    headerName: "Revenue",
                    field: "CAD_PayIn_Revenue",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                  {
                    headerName: "Volume",
                    field: "CAD_PayIn_Volume",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                ],
              },
              {
                headerName: "Pay-Out",
                children: [
                  {
                    headerName: "Revenue",
                    field: "CAD_PayOut_Revenue",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                  {
                    headerName: "Volume",
                    field: "CAD_PayOut_Volume",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                ],
              },
              {
                headerName: "Total",
                children: [
                  {
                    headerName: "Revenue",
                    field: "CAD_Total_Revenue",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                  {
                    headerName: "Volume",
                    field: "CAD_Total_Volume",
                    valueFormatter: (p) =>
                      constantFunctions.formatRevenue(p.value),
                    flex: 1,
                    minWidth: 80,
                    maxWidth: 120,
                    headerClass: "ag-header-cell-custom",
                    cellClass: "ag-cell-custom",
                  },
                ],
              },
            ],
          },
        ],
      },

      {
        headerName: "B2B Global",
        children: [
          {
            headerName: "Revenue",
            field: "B2B_Global_Revenue",
            valueFormatter: (p) => constantFunctions.formatRevenue(p.value),
            flex: 1,
            minWidth: 80,
            maxWidth: 120,
            headerClass: "ag-header-cell-custom",
            cellClass: "ag-cell-custom",
          },
          {
            headerName: "Volume",
            field: "B2B_Global_Volume",
            valueFormatter: (p) => constantFunctions.formatRevenue(p.value),
            flex: 1,
            minWidth: 80,
            maxWidth: 120,
            headerClass: "ag-header-cell-custom",
            cellClass: "ag-cell-custom",
          },
        ],
      },

      {
        headerName: "Consumer Global",
        children: [
          {
            headerName: "Revenue",
            field: "Consumer_Global_Revenue",
            valueFormatter: (p) => constantFunctions.formatRevenue(p.value),
            flex: 1,
            minWidth: 80,
            maxWidth: 120,
            headerClass: "ag-header-cell-custom",
            cellClass: "ag-cell-custom",
          },
          {
            headerName: "Volume",
            field: "Consumer_Global_Volume",
            valueFormatter: (p) => constantFunctions.formatRevenue(p.value),
            flex: 1,
            minWidth: 80,
            maxWidth: 120,
            headerClass: "ag-header-cell-custom",
            cellClass: "ag-cell-custom",
          },
        ],
      },

      {
        headerName: "Total Global",
        children: [
          {
            headerName: "Revenue",
            field: "Total_Global_Revenue",
            valueFormatter: (p) => constantFunctions.formatRevenue(p.value),
            flex: 1,
            minWidth: 80,
            maxWidth: 120,
            headerClass: "ag-header-cell-custom",
            cellClass: "ag-cell-custom",
            cellStyle: (params) => {
              const value = Number(params.value || 0);
              if (value > 0) {
                return {
                  color: "#15803d",
                  backgroundColor: "#dcfce7",
                  fontWeight: 600,
                  borderRight: "1px solid #d1d5db",
                  borderBottom: "1px solid #d1d5db",
                };
              }
              if (value < 0) {
                return {
                  color: "#dc2626",
                  backgroundColor: "#fee2e2",
                  fontWeight: 600,
                  borderRight: "1px solid #d1d5db",
                  borderBottom: "1px solid #d1d5db",
                };
              }
              return {
                color: "#6b7280",
                backgroundColor: "#f3f4f6",
                fontWeight: 600,
                borderRight: "1px solid #d1d5db",
                borderBottom: "1px solid #d1d5db",
              };
            },
          },
          {
            headerName: "Volume",
            field: "Total_Global_Volume",
            valueFormatter: (p) => constantFunctions.formatRevenue(p.value),
            flex: 1,
            minWidth: 80,
            maxWidth: 120,
            headerClass: "ag-header-cell-custom",
            cellClass: "ag-cell-custom",
            cellStyle: (params) => {
              const value = Number(params.value || 0);
              if (value > 0) {
                return {
                  color: "#15803d",
                  backgroundColor: "#dcfce7",
                  fontWeight: 600,
                  borderRight: "1px solid #d1d5db",
                  borderBottom: "1px solid #d1d5db",
                };
              }
              if (value < 0) {
                return {
                  color: "#dc2626",
                  backgroundColor: "#fee2e2",
                  fontWeight: 600,
                  borderRight: "1px solid #d1d5db",
                  borderBottom: "1px solid #d1d5db",
                };
              }
              return {
                color: "#6b7280",
                backgroundColor: "#f3f4f6",
                fontWeight: 600,
                borderRight: "1px solid #d1d5db",
                borderBottom: "1px solid #d1d5db",
              };
            },
          },
        ],
      },
    ],
    [typeFilter],
  );

  return (
    <>
      {/* Header */}
      <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-row justify-between items-center px-4 sticky top-0 bg-white z-10 w-full">
        <p className="text-text-base text-[14px] font-semibold">Revenue</p>
        <div className="flex items-center gap-2">
          <LightCustomButton
            onClick={getRevenue}
            text="LOAD REVENUE"
            isActive={true}
            isButtonLoading={loading}
          />
          <LightCustomButton
            onClick={exportToCSV}
            text="EXPORT CSV"
            isActive={true}
          />
        </div>
      </div>

      <div className="flex flex-col overflow-y-auto">
        {/* Filter pills */}
        <div className="flex space-x-3 py-3 px-4 w-full">
          {["Monthly", "Daily"].map((type) => (
            <div
              key={type}
              onClick={
                loading
                  ? undefined
                  : () =>
                      handleTypeFilterChange(type as "Monthly" | "Daily")
              }
              className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              } ${
                typeFilter === type
                  ? "border-border-button-secondary bg-bg-button-secondary"
                  : "border-border-subtle bg-bg-page-body"
              }`}
            >
              {type}
            </div>
          ))}
        </div>

        {/* AG Grid - full width with horizontal scroll */}
        <div className="w-full overflow-x-auto px-4" style={{ height: "45vh" }}>
          {loading ? (
            <AgGridSkeleton
              columnDefs={columnDefs}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: false,
              }}
              centerAlignHeaders={true}
              style={{ width: "100%", height: "100%" }}
            />
          ) : (
            <AgGridReactTable
              ref={gridRef}
              rowData={data}
              columnDefs={columnDefs}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: false,
              }}
              centerAlignHeaders={true}
              error={error ?? undefined}
              style={{ width: "100%", height: "100%" }}
            />
          )}
        </div>

        {/* Charts - vertically stacked, each full-width */}
        <div className="w-full flex flex-col gap-4 p-4 bg-bg-page-body">
          <DualAxisChart
            data={data}
            title="CPG: Pay-in"
            revenueKey="CPG_PayIn_Revenue"
            volumeKey="CPG_PayIn_Volume"
            dataType={typeFilter}
          />
          <DualAxisChart
            data={data}
            title="CPG: Pay-out"
            revenueKey="CPG_PayOut_Revenue"
            volumeKey="CPG_PayOut_Volume"
            dataType={typeFilter}
          />
          <DualAxisChart
            data={data}
            title="CPG: Total"
            revenueKey="CPG_Total_Revenue"
            volumeKey="CPG_Total_Volume"
            dataType={typeFilter}
          />
          <DualAxisChart
            data={data}
            title="Cross-Ramp: Pay-in (IND)"
            revenueKey="XR_India_PayIn_Revenue"
            volumeKey="XR_India_PayIn_Volume"
            dataType={typeFilter}
          />
          <DualAxisChart
            data={data}
            title="Cross-Ramp: Pay-in (BRL)"
            revenueKey="XR_BRL_PayIn_Revenue"
            volumeKey="XR_BRL_PayIn_Volume"
            dataType={typeFilter}
          />
          <DualAxisChart
            data={data}
            title="Cross-Ramp: Pay-out (BRL)"
            revenueKey="XR_BRL_PayOut_Revenue"
            volumeKey="XR_BRL_PayOut_Volume"
            dataType={typeFilter}
          />
          <DualAxisChart
            data={data}
            title="Cross-Ramp: Pay-in (EU)"
            revenueKey="OpenBanking_PayIn_Revenue"
            volumeKey="OpenBanking_PayIn_Volume"
            dataType={typeFilter}
          />
          <DualAxisChart
            data={data}
            title="Cross-Ramp: Pay-out (EU)"
            revenueKey="OpenBanking_PayOut_Revenue"
            volumeKey="OpenBanking_PayOut_Volume"
            dataType={typeFilter}
          />
          <DualAxisChart
            data={data}
            title="Cross-Ramp: Pay-in (CAD)"
            revenueKey="CAD_PayIn_Revenue"
            volumeKey="CAD_PayIn_Volume"
            dataType={typeFilter}
          />
          <DualAxisChart
            data={data}
            title="Cross-Ramp: Pay-out (CAD)"
            revenueKey="CAD_PayOut_Revenue"
            volumeKey="CAD_PayOut_Volume"
            dataType={typeFilter}
          />
          <DualAxisChart
            data={data}
            title="Total Merchant Business"
            revenueKey="B2B_Global_Revenue"
            volumeKey="B2B_Global_Volume"
            dataType={typeFilter}
          />
          <DualAxisChart
            data={data}
            title="Total Customer Business"
            revenueKey="Consumer_Global_Revenue"
            volumeKey="Consumer_Global_Volume"
            dataType={typeFilter}
          />
          <DualAxisChart
            data={data}
            title="Total Business"
            revenueKey="Total_Global_Revenue"
            volumeKey="Total_Global_Volume"
            dataType={typeFilter}
          />
        </div>
      </div>
    </>
  );
};

export default MobileRevenue;
